import { OneStemBrand } from '@/components/brand/OneStemBrand'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { useSignUpMutation } from '@/lib/services/auth/auth.query'
import { signUpSchema } from '@/lib/services/auth/auth.schema'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'

export const Route = createFileRoute('/(auth)/sign-up')({
  component: RouteComponent,
  // Currently, sign-up is not supported because we can't auto-link them to an organization.
  // However, it might be supported at some future point, so I'll leave most of this here.
  beforeLoad: () => {
    throw redirect({ to: '/login' })
  },
  loader: () => ({ isReadOnly: false }),
})

function RouteComponent() {
  const signUpMutation = useSignUpMutation()
  const navigate = useNavigate({ from: '/sign-up' })

  const form = useAppForm({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
    },
    validators: {
      onSubmit: signUpSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await signUpMutation.mutateAsync(value, {
            onSuccess: () => {
              toast.success('Account created successfully')
              void navigate({ to: '/admin' })
            },
          })
        } catch (error) {
          toast.error('Account creation failed', {
            description: getErrorMessage(error),
          })
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })
  return (
    <div className="flex flex-col gap-y-4 h-screen items-center justify-center px-8 sm:px-0">
      <Link to="/">
        <OneStemBrand className="h-20" />
      </Link>
      <Card className="w-full sm:max-w-md">
        <CardHeader>
          <CardTitle>Sign Up</CardTitle>
          <CardDescription>Enter your information to create a new account</CardDescription>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.Form>
              <div className="grid w-full items-center gap-6">
                <form.FormError />
                <form.AppField name="name" validators={{ onChange: signUpSchema.shape.name }}>
                  {(field) => <field.TextInput id="name" label="Name" placeholder="Your name" />}
                </form.AppField>
                <form.AppField name="email" validators={{ onChange: signUpSchema.shape.email }}>
                  {(field) => <field.EmailInput label="Email" />}
                </form.AppField>
                <form.AppField
                  name="password"
                  validators={{ onChange: signUpSchema.shape.password }}
                >
                  {(field) => <field.TextInput id="password" label="Password" type="password" />}
                </form.AppField>
                <form.AppField
                  name="password_confirmation"
                  validators={{
                    onChangeListenTo: ['password'],
                    onChange: ({ value, fieldApi }) => {
                      if (value !== fieldApi.form.getFieldValue('password'))
                        return 'Passwords do not match'
                    },
                  }}
                >
                  {(field) => (
                    <field.TextInput
                      id="password_confirmation"
                      label="Confirm Password"
                      type="password"
                    />
                  )}
                </form.AppField>
                <div>
                  <form.SubmitButton mutation={signUpMutation}>Sign up</form.SubmitButton>
                </div>
              </div>
            </form.Form>
          </form.AppForm>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Typography variant="label">
            Already have an account?{' '}
            <Link to="/login">
              <Typography variant="label" tone="interaction" interaction="link">
                Login
              </Typography>
            </Link>
          </Typography>
        </CardFooter>
      </Card>
    </div>
  )
}
